import { Animation, Color4, EventState, QuadraticEase } from '@babylonjs/core';
import {
  AdvancedDynamicTexture,
  Button,
  Checkbox,
  Container,
  Control,
  Grid,
  InputText,
  Rectangle,
  Slider,
  StackPanel,
  TextBlock,
  Vector2WithInfo,
} from '@babylonjs/gui';
import { Game } from '../Game';
import { Gui } from './Gui';

export class Gui2D {
  ui: AdvancedDynamicTexture;
  fadeOutAnimation: Animation;
  fadeInAnimation: Animation;

  constructor(public game: Game) {
    this.ui = AdvancedDynamicTexture.CreateFullscreenUI('ui', true, this.game.guiScene);

    this.fadeOutAnimation = this.buildAnimation('alpha', 1, 0, 60, Animation.ANIMATIONTYPE_FLOAT);
    this.fadeInAnimation = this.buildAnimation('alpha', 0, 1, 60, Animation.ANIMATIONTYPE_FLOAT);
  }

  buildAnimation(property: string, from: any, to: any, duration: number, type: number): Animation {
    const anim = new Animation('fadeAnim', property, 30, type, Animation.ANIMATIONLOOPMODE_CONSTANT);
    anim.setKeys([
      { frame: 0, value: from },
      { frame: duration, value: to },
    ]);
    const easingFunction = new QuadraticEase();
    anim.setEasingFunction(easingFunction);
    return anim;
  }

  fadeIn(control: Control, onAnimationEnd?: () => void) {
    control.alpha = 0;
    control.isVisible = true;
    this.game.scene.beginDirectAnimation(control, [this.fadeInAnimation], 0, 60, false, 1, onAnimationEnd);
  }

  fadeOut(control: Control, onAnimationEnd?: () => void) {
    this.game.scene.beginDirectAnimation(control, [this.fadeOutAnimation], 0, 60, false, 1, () => {
      control.isVisible = false;
      if (onAnimationEnd) {
        onAnimationEnd();
      }
    });
  }

  createProgressBar() {
    const progressBar = new Rectangle();
    progressBar.width = 0.5;
    progressBar.height = '35px';
    progressBar.verticalAlignment = 1;
    progressBar.top = '-50px';
    progressBar.isVisible = false;
    progressBar.background = 'black';
    this.ui.addControl(progressBar);

    const progressBarInner = new Rectangle();
    progressBarInner.width = 0;
    progressBarInner.height = '33px'; // progressBar.height - (progressBar.thickness *2 )
    progressBarInner.thickness = 0;
    progressBarInner.horizontalAlignment = 0;
    progressBarInner.isVisible = true;
    progressBarInner.background = 'green';
    progressBar.addControl(progressBarInner);

    const setProgress = (progress = 0) => {
      progressBarInner.width = progress / 100;
    };

    const showProgressBar = (progress: number) => {
      if (progress) {
        setProgress(progress);
      }
      progressBar.isVisible = true;
    };

    const hideProgressBar = () => {
      progressBar.isVisible = false;
    };

    return progressBar;
  }

  createGrid(
    name: string,
    width: string,
    height: string,
    rows: number,
    cols: number,
    padding: number,
    controls?: (grid: Grid, row: number, column: number, index: number) => Control
  ): Grid {
    const grid = new Grid(name);
    grid.width = width;
    grid.height = height;

    // grid.paddingLeftInPixels = padding;
    // grid.paddingRightInPixels = padding;
    // grid.paddingTopInPixels = padding;
    // grid.paddingBottomInPixels = padding;

    for (let i = 0; i < rows; i++) {
      grid.addRowDefinition(1 / rows);
    }

    for (let j = 0; j < cols; j++) {
      grid.addColumnDefinition(1 / cols);
    }

    this.ui.addControl(grid);

    if (controls) {
      let i = 0;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const control = controls(grid, r, c, i);
          if (control) {
            control.paddingLeftInPixels = padding;
            control.paddingRightInPixels = padding;
            control.paddingTopInPixels = padding;
            control.paddingBottomInPixels = padding;

            grid.addControl(control, r, c);
          }
          i++;
        }
      }
    }

    return grid;
  }

  clear() {
    this.ui.clear();
  }

  createRectangle(width: string | number, height: string | number, container?: Container): Rectangle {
    const frame = new Rectangle();
    frame.width = width;
    frame.height = height;
    frame.background = 'transparent';
    frame.color = Gui.primaryColor.toHexString();

    if (container !== undefined) {
      container.addControl(frame);
    } else {
      this.ui.addControl(frame);
    }

    return frame;
  }

  createTextBlock(name?: string, text?: string, container?: Container, fontSize: string = Gui.fontSizeM): TextBlock {
    const textBlock = new TextBlock(name, text);

    textBlock.color = Gui.primaryColor.toHexString();
    textBlock.fontSize = fontSize;
    textBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    textBlock.fontFamily = Gui.fontFamily;

    Gui.setDefaultShadow(textBlock);

    if (container !== undefined && container !== null && !(container as any).isDisposed) {
      container.addControl(textBlock);
    }

    return textBlock;
  }

  createButton(
    name: string,
    txt: string,
    callback: (eventData: Vector2WithInfo, eventState: EventState) => void,
    container?: Container,
    alpha?: number,
    colors?: {
      foreground?: Color4;
      background?: Color4;
      disableHover?: boolean;
    }
  ): Button {
    const button: Button = Button.CreateSimpleButton(name, txt);
    if (container !== undefined && container !== null && !(container as any).isDisposed) {
      container.addControl(button);
    }
    button.fontSize = Gui.fontSizeM;
    if (alpha !== undefined) {
      button.alpha = alpha;
    }
    button.fontFamily = Gui.fontFamily;
    button.onPointerUpObservable.add(callback);

    const fg = colors?.foreground ?? Gui.primaryColor;
    const bg = colors?.background ?? Gui.backgroundColor;

    button.color = fg.toHexString();
    button.background = bg.toHexString();

    Gui.setDefaultShadow(button.children[0]);

    if (!colors?.disableHover) {
      button.pointerOutAnimation = () => {
        button.color = fg.toHexString();
        button.background = bg.toHexString();
      };

      button.pointerEnterAnimation = () => {
        button.color = bg.toHexString();
        button.background = fg.toHexString();
      };
    }

    Gui.decorateElement(this.game, button);

    return button;
  }

  createTextField(
    name: string,
    text: string,
    callback: (text: string) => void,
    container?: Container,
    placeholderText?: string
  ): InputText {
    const input = new InputText(name, text);
    if (placeholderText) {
      input.placeholderText = placeholderText;
    }
    input.color = Gui.primaryColor.toHexString();
    input.background = Gui.backgroundColor.toHexString();
    input.focusedBackground = Gui.backgroundColor.toHexString();
    input.placeholderColor = 'gray';
    input.fontSize = Gui.fontSizeXL;
    input.fontFamily = Gui.fontFamily;

    input.onTextChangedObservable.add((input) => {
      callback(input.text);
    });

    Gui.setDefaultShadow(input);

    if (container && !(container as any).isDisposed) {
      container.addControl(input);
    }

    return input;
  }

  createCheckbox(
    name: string,
    text: string,
    isChecked: boolean,
    callback: (isChecked: boolean) => void,
    container?: Container
  ): StackPanel {
    const panel = new StackPanel(name + '_panel');
    panel.isVertical = false;
    panel.height = '6%';
    panel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;

    const textBlock = new TextBlock(name + '_text', text);
    textBlock.color = Gui.primaryColor.toHexString();
    textBlock.fontSize = Gui.fontSizeM;
    textBlock.fontFamily = Gui.fontFamily;
    textBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    textBlock.width = '45%';

    panel.addControl(textBlock);

    const checkbox = new Checkbox(name);
    checkbox.width = Gui.fontSizeM;
    checkbox.height = Gui.fontSizeM;
    checkbox.isChecked = isChecked;
    checkbox.color = Gui.secondaryColor.toHexString();
    checkbox.thickness = 1;
    checkbox.background = 'rgba(0,0,0,0.5)';
    checkbox.onIsCheckedChangedObservable.add((value) => {
      callback(value);
    });
    panel.addControl(checkbox);

    if (container) {
      container.addControl(panel);
    }

    return panel;
  }

  createSlider(
    name: string,
    text: string,
    value: number,
    callback: (value: number) => void,
    container?: Container,
    min: number = 0,
    max: number = 1,
    step: number = 0.01
  ): StackPanel {
    const panel = new StackPanel(name + '_panel');
    panel.isVertical = false;
    panel.height = '6%';
    panel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;

    const textBlock = new TextBlock(name + '_text', text);
    textBlock.color = Gui.primaryColor.toHexString();
    textBlock.fontSize = Gui.fontSizeM;
    textBlock.fontFamily = Gui.fontFamily;
    textBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    textBlock.width = '35%';
    panel.addControl(textBlock);

    const slider = new Slider(name);
    slider.minimum = min;
    slider.maximum = max;
    slider.step = step;
    slider.value = value;
    slider.width = Gui.fontSizeXXXL;
    slider.height = Gui.fontSizeXS;
    slider.color = Gui.secondaryColor.toHexString();
    slider.background = 'rgba(0,0,0,0.5)';
    slider.thumbColor = Gui.primaryColor.toHexString();
    slider.borderColor = Gui.secondaryColor.toHexString();
    slider.isThumbCircle = true;
    slider.onValueChangedObservable.add((value) => {
      callback(value);
      valueText.text = Math.round(value * 100) + '%';
    });
    panel.addControl(slider);

    const valueText = new TextBlock(name + '_value', Math.round(value * 100) + '%');
    valueText.color = Gui.primaryColor.toHexString();
    valueText.fontSize = Gui.fontSizeM;
    valueText.fontFamily = Gui.fontFamily;
    valueText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    valueText.paddingLeft = '10px';
    valueText.width = '10%';
    panel.addControl(valueText);

    if (container) {
      container.addControl(panel);
    } else {
      this.ui.addControl(panel);
    }

    return panel;
  }

  styleSceneButton(button: Button) {
    button.fontSize = Gui.fontSizeM;
    button.thickness = 1;
    button.width = '25%';
    button.height = '8%';
    button.background = 'rgba(0,0,0,0.5)';
    button.cornerRadius = 10;
  }

  sceneButtonColors(): any {
    return {
      background: new Color4(1, 1, 1, 0),
      disableHover: true,
    };
  }
}
